import { Box, SxProps, Theme } from "@mui/material";
import { FolderItem } from "../../interface";
import { isRecentlyImported } from "../TemplateLibraryService";
import templateLibraryStyles from "../TemplateLibrary.styles";
import { KitGlyph, LibraryGlyph } from "../TemplateLibraryIcons";

const styles = templateLibraryStyles;

/** A My Templates item that was imported from the Template Library. */
export const isFromLibrary = (item: FolderItem) => !!item.libraryTemplateId;

/** An imported kit — one template carrying several documents. */
export const isLibraryKit = (item: FolderItem) =>
  isFromLibrary(item) && (item.files?.length ?? 0) > 1;

/** Card sub-line for an imported item, or `null` to keep the default. */
export const librarySubLabel = (item: FolderItem) => {
  if (!isFromLibrary(item)) return null;
  return isLibraryKit(item)
    ? `Kit · ${item.files.length} documents`
    : "Imported from Template library";
};

/** Warm card tint the design gives library-sourced items. */
export const libraryCardSx = (item: FolderItem): SxProps<Theme> =>
  isFromLibrary(item)
    ? {
        bgcolor: styles.importedCardBg,
        borderColor: styles.importedCardBorder,
      }
    : {};

/** KIT · FROM LIBRARY · NEW pills; renders nothing for other items. */
const LibrarySourceBadges = ({
  item,
  showNew = true,
  sx,
}: {
  item: FolderItem;
  showNew?: boolean;
  sx?: SxProps<Theme>;
}) => {
  if (!isFromLibrary(item)) return null;
  return (
    <Box
      sx={[
        { display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {isLibraryKit(item) && (
        <Box component="span" sx={styles.kitPill}>
          <KitGlyph sx={{ width: 11, height: 11 }} />
          KIT
        </Box>
      )}
      <Box component="span" sx={styles.fromLibraryPill}>
        <LibraryGlyph sx={{ width: 11, height: 11 }} />
        FROM LIBRARY
      </Box>
      {showNew && isRecentlyImported(item.id) && (
        <Box component="span" sx={styles.newPill}>
          NEW
        </Box>
      )}
    </Box>
  );
};

export default LibrarySourceBadges;
